// AudioManager — the single owner of all audio state (BP-AUDIO-SYSTEM-01).
//
// A plain-JS singleton (class + one exported instance). No audio state lives in
// React components; the thin useAudioPrefs hook only mirrors getPrefs() for
// rendering toggles. Every path/key resolves through src/config/audioManifest.js
// — components never hand-build an /assets/audio/... string.
//
// SHIPS SILENT: the audio files don't exist until BP-AUDIO-CONTENT-01. Every
// method here is a safe no-op when its asset is absent — missing sprite/files
// must never crash init or any call. In dev, a startup reconciliation table and
// play-time warnings make the ❌-missing set visible; in production all of that
// is skipped and absence is a pure silent no-op.
//
// iOS autoplay: handled by Howler's built-in unlock-on-first-gesture. BGM not
// starting before the very first tap is expected, not a bug — no manual code.

import { Howl, Howler } from 'howler';
import {
  AUDIO_MANIFEST,
  AUDIO_BASE_PATH,
  SFX_SPRITE_MANIFEST_FILE,
  getEntry,
  fileUrl,
  launchKeys,
} from '../config/audioManifest';

const LS = {
  muted: 'ww-audio-muted',
  bgmBiome: 'ww-audio-bgm-biome',
  bgmReader: 'ww-audio-bgm-reader',
  sfx: 'ww-audio-sfx',
};

// Default master volume on first launch — loud enough to be clearly audible,
// soft enough not to startle (audio-system.md → "Default volume").
const DEFAULT_VOLUME = 0.8;
const CROSSFADE_MS = 1500;
const AMBIENT_KEY = 'story-generating';

const isDev = !!import.meta.env.DEV;

function readBool(key, fallback) {
  try {
    const v = localStorage.getItem(key);
    if (v === null) return fallback;
    return v === 'true';
  } catch {
    return fallback;
  }
}

function writeBool(key, value) {
  try {
    localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    /* localStorage unavailable (private mode / SSR) — prefs stay in-memory */
  }
}

class AudioManager {
  constructor() {
    // Granular prefs (device-level, localStorage). Master mute is separate and
    // never mutates the three granular prefs.
    this.prefs = {
      muted: readBool(LS.muted, false),
      bgmBiome: readBool(LS.bgmBiome, true),
      bgmReader: readBool(LS.bgmReader, true),
      sfxEnabled: readBool(LS.sfx, true),
    };

    this.masterVolume = DEFAULT_VOLUME;

    // Intended BGM track + context (recorded even when a pref/mute gates actual
    // playback, so un-mute / enabling a toggle can resume the right track).
    this.currentTrack = null;
    this.currentContext = null;

    // Active Howl instances. Created lazily; null until first (allowed) play.
    this.bgmHowl = null;
    this._playingKey = null; // key the bgmHowl actually holds
    this.sfxHowl = null;
    this.ambientHowl = null;

    // SFX sprite keys present in the loaded manifest (empty until/unless the
    // sprite JSON loads). Drives the play-time present-check.
    this.spriteKeys = new Set();

    this._initialised = false;

    if (typeof window !== 'undefined') {
      // Apply master mute state immediately so nothing is ever audible while
      // muted, even before the first track loads.
      try {
        Howler.volume(this.prefs.muted ? 0 : this.masterVolume);
      } catch {
        /* Howler not ready — harmless */
      }
      this.init();
    }
  }

  // Async, fire-and-forget, never throws. Loads the SFX sprite (if present) and
  // runs the dev-only reconciliation table.
  async init() {
    if (this._initialised) return;
    this._initialised = true;
    await this._loadSprite();
    if (isDev) this._reconcile();
  }

  // ── SFX sprite ──────────────────────────────────────────────────────────
  // Fetch the audiosprite manifest (howler/howler2 JSON). If it's absent (no
  // assets yet) the fetch 404s, we catch it, and the sprite stays empty so every
  // playSFX is a silent no-op. Constructing the Howl needs the sprite map, so we
  // only build it once the JSON has actually loaded — that also avoids requesting
  // the .mp3 at all when nothing has been generated.
  async _loadSprite() {
    const manifestUrl = `${AUDIO_BASE_PATH}/${SFX_SPRITE_MANIFEST_FILE}`;
    let manifest;
    try {
      const res = await fetch(manifestUrl);
      if (!res.ok) return; // 404 = sprite not generated yet → stay silent
      manifest = await res.json();
    } catch {
      return; // network/parse failure → stay silent, never throw
    }

    const sprite = manifest?.sprite;
    const srcList = manifest?.src || manifest?.urls;
    if (!sprite || !srcList || srcList.length === 0) return;

    // audiosprite emits bare filenames — resolve them under the sfx folder.
    const src = srcList.map((f) =>
      f.startsWith('http') || f.startsWith('/') ? f : `${AUDIO_BASE_PATH}/sfx/${f}`
    );

    try {
      this.sfxHowl = new Howl({
        src,
        sprite,
        preload: true,
        onloaderror: () => {
          // Audio data missing/corrupt — drop back to silent no-op, no crash.
          this.sfxHowl = null;
          this.spriteKeys = new Set();
        },
      });
      this.spriteKeys = new Set(Object.keys(sprite));
    } catch {
      this.sfxHowl = null;
      this.spriteKeys = new Set();
    }
  }

  // ── Dev reconciliation table ──────────────────────────────────────────────
  // Diffs the registry's LAUNCH keys against what is actually loadable:
  //   SFX  → present iff the key exists in the loaded sprite manifest.
  //   BGM/ambient → present iff a HEAD request for its file succeeds.
  // Prints ✅ present / ❌ missing, plus ⚠️ extra sprite keys not in the registry.
  // Dev-only, fully guarded, never throws — production never calls this.
  async _reconcile() {
    try {
      const launch = new Set(launchKeys());
      const rows = [];

      const fileEntries = AUDIO_MANIFEST.filter(
        (e) => (e.kind === 'bgm' || e.kind === 'ambient') && launch.has(e.key)
      );
      const presence = await Promise.all(
        fileEntries.map((e) => this._fileReachable(fileUrl(e.key)))
      );
      fileEntries.forEach((e, i) => {
        rows.push({
          key: e.key,
          kind: e.kind,
          status: presence[i] ? '✅ present' : '❌ missing',
        });
      });

      AUDIO_MANIFEST.filter((e) => e.kind === 'sfx' && launch.has(e.key)).forEach((e) => {
        rows.push({
          key: e.key,
          kind: 'sfx',
          status: this.spriteKeys.has(e.key) ? '✅ present' : '❌ missing',
        });
      });

      // ⚠️ sprite keys with no matching registry entry.
      this.spriteKeys.forEach((k) => {
        if (!getEntry(k)) rows.push({ key: k, kind: 'sfx', status: '⚠️ extra (not in registry)' });
      });

      const missing = rows.filter((r) => r.status.startsWith('❌')).length;
      console.groupCollapsed(
        `%c[audio] reconciliation — ${rows.length - missing}/${rows.length} launch assets present`,
        'color:#0a7'
      );
      console.table(rows);
      if (missing > 0) {
        console.info(
          `[audio] ${missing} launch asset(s) not generated yet — playback is a safe no-op until BP-AUDIO-CONTENT-01.`
        );
      }
      console.groupEnd();
    } catch {
      /* reconciliation is best-effort diagnostics — never let it break boot */
    }
  }

  async _fileReachable(url) {
    if (!url) return false;
    try {
      const res = await fetch(url, { method: 'HEAD' });
      return res.ok;
    } catch {
      return false;
    }
  }

  // ── Gating ────────────────────────────────────────────────────────────────
  _bgmAllowed(context) {
    if (this.prefs.muted) return false;
    if (context === 'biome' && !this.prefs.bgmBiome) return false;
    if (context === 'reader' && !this.prefs.bgmReader) return false;
    return true;
  }

  // ── BGM ─────────────────────────────────────────────────────────────────
  playBGM(key, context) {
    // Record intent regardless of gating, so un-mute / enabling a toggle resumes
    // the correct track.
    this.currentTrack = key;
    this.currentContext = context;

    const entry = getEntry(key);
    if (!entry || (entry.kind !== 'bgm' && entry.kind !== 'ambient')) {
      if (isDev) console.warn(`[audio] playBGM: unknown/invalid BGM key "${key}"`);
      return;
    }

    if (!this._bgmAllowed(context)) return; // muted or disabled by pref → no-op
    if (this.bgmHowl && this._playingKey === key) return; // already playing it

    this._startBgm(key);
  }

  _startBgm(key) {
    const url = fileUrl(key);
    if (!url) return;

    // Crossfade: fade the outgoing track out and unload it after the fade.
    this._fadeOutAndUnload(this.bgmHowl);
    this.bgmHowl = null;
    this._playingKey = null;

    let howl;
    try {
      howl = new Howl({
        src: [url],
        loop: true,
        html5: false, // Web Audio: small seamless loops (~75-85s, <=1.5MB) decode
                      // cheaply, only one buffer live at a time, and the HTML5 pool
                      // handed back locked audio objects → silent BGM (FP-AUDIO-BGM-NO-SOUND-01)
        volume: 0, // fade in from silence
        onloaderror: () => {
          // File not generated yet (or failed) — make it visible in dev, then
          // tear down so we never leave a dead Howl around. Never throws.
          if (isDev) console.warn(`[audio] BGM file missing: "${key}" (${url})`);
          if (this.bgmHowl === howl) {
            this.bgmHowl = null;
            this._playingKey = null;
          }
          try { howl.unload(); } catch { /* already gone */ }
        },
        onplayerror: () => {
          // iOS autoplay lock etc. — Howler retries on next gesture; nothing to do.
        },
      });
      howl.play();
      howl.fade(0, 1, CROSSFADE_MS);
    } catch {
      // Construction should not throw, but stay crash-proof regardless.
      return;
    }

    this.bgmHowl = howl;
    this._playingKey = key;
  }

  stopBGM() {
    this._fadeOutAndUnload(this.bgmHowl);
    this.bgmHowl = null;
    this._playingKey = null;
    // Intent is cleared too: an explicit stop means "no BGM for now".
    this.currentTrack = null;
    this.currentContext = null;
  }

  _fadeOutAndUnload(howl) {
    if (!howl) return;
    try {
      howl.fade(howl.volume(), 0, CROSSFADE_MS);
      howl.once('fade', () => {
        try { howl.stop(); howl.unload(); } catch { /* already gone */ }
      });
    } catch {
      try { howl.unload(); } catch { /* already gone */ }
    }
  }

  // ── SFX ───────────────────────────────────────────────────────────────────
  playSFX(key) {
    if (this.prefs.muted || !this.prefs.sfxEnabled) return;
    if (!this.sfxHowl || !this.spriteKeys.has(key)) {
      if (isDev) console.warn(`[audio] playSFX: no loaded sample for "${key}" — no-op (asset not generated yet?)`);
      return;
    }
    try {
      this.sfxHowl.play(key);
    } catch {
      /* never let a sound effect crash a handler */
    }
  }

  // ── Ambient (story-generating loop) ────────────────────────────────────────
  playAmbient() {
    if (this.prefs.muted) return; // master mute → no-op
    if (this.ambientHowl) return; // already looping
    const url = fileUrl(AMBIENT_KEY);
    if (!url) return;
    let howl;
    try {
      howl = new Howl({
        src: [url],
        loop: true,
        html5: false, // Web Audio (FP-AUDIO-BGM-NO-SOUND-01) — see _startBgm
        volume: 1,
        onloaderror: () => {
          if (isDev) console.warn(`[audio] ambient file missing: "${AMBIENT_KEY}" (${url})`);
          if (this.ambientHowl === howl) this.ambientHowl = null;
          try { howl.unload(); } catch { /* already gone */ }
        },
        onplayerror: () => { /* autoplay lock — Howler retries on gesture */ },
      });
      howl.play();
    } catch {
      return;
    }
    this.ambientHowl = howl;
  }

  stopAmbient() {
    this._fadeOutAndUnload(this.ambientHowl);
    this.ambientHowl = null;
  }

  // ── Prefs ─────────────────────────────────────────────────────────────────
  setMuted(value) {
    const muted = !!value;
    this.prefs.muted = muted;
    writeBool(LS.muted, muted);
    try {
      // Master switch: silence everything instantly without touching the
      // granular prefs (Howler.volume scales all howls).
      Howler.volume(muted ? 0 : this.masterVolume);
    } catch {
      /* harmless */
    }
    if (!muted) {
      // Resume the intended track if it isn't already loaded/playing.
      if (this.currentTrack && this._bgmAllowed(this.currentContext) && !this.bgmHowl) {
        this._startBgm(this.currentTrack);
      }
    }
  }

  setBgmBiomeEnabled(value) {
    const on = !!value;
    this.prefs.bgmBiome = on;
    writeBool(LS.bgmBiome, on);
    if (this.currentContext === 'biome') {
      if (on && !this.prefs.muted) {
        if (!this.bgmHowl) this._startBgm(this.currentTrack);
      } else if (!on) {
        this._fadeOutAndUnload(this.bgmHowl);
        this.bgmHowl = null;
        this._playingKey = null;
      }
    }
  }

  setBgmReaderEnabled(value) {
    const on = !!value;
    this.prefs.bgmReader = on;
    writeBool(LS.bgmReader, on);
    if (this.currentContext === 'reader') {
      if (on && !this.prefs.muted) {
        if (!this.bgmHowl) this._startBgm(this.currentTrack);
      } else if (!on) {
        this._fadeOutAndUnload(this.bgmHowl);
        this.bgmHowl = null;
        this._playingKey = null;
      }
    }
  }

  setSFXEnabled(value) {
    const on = !!value;
    this.prefs.sfxEnabled = on;
    writeBool(LS.sfx, on);
  }

  getPrefs() {
    return { ...this.prefs };
  }
}

// One shared instance for the whole app.
const audioManager = new AudioManager();
export default audioManager;
